"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 19: Namco 163 (N163)
 * ------------------------------------------------------------------ *
 * The number 19 used to be called "Namcot 106", but that chip was never
 * found on a board: the boards carry a Namco 163 (or a 129, 175 or 340).
 * NESdev, "INES Mapper 019":
 *
 *   CPU $6000-$7FFF  8 KB of battery-backed PRG RAM (optional)
 *   CPU $8000-$9FFF  8 KB switchable PRG bank
 *   CPU $A000-$BFFF  8 KB switchable PRG bank
 *   CPU $C000-$DFFF  8 KB switchable PRG bank
 *   CPU $E000-$FFFF  8 KB fixed to the last bank
 *   PPU $0000-$1FFF  eight 1 KB switchable CHR banks
 *
 * The registers live in $8000-$FFFF, one per $800 block:
 *
 *   $8000-$BFFF  CHR: one 1 KB window per $800 block, so $8000, $8800,
 *                $9000, $9800 feed $0000-$0FFF and $A000, $A800, $B000,
 *                $B800 feed $1000-$1FFF
 *   $C000-$DFFF  which of the console's two nametable pages shows in each
 *                of the four screen areas
 *   $E000        PRG bank for $8000 (6 bits); bit 6 disables the sound
 *   $E800        PRG bank for $A000 (6 bits); bits 6-7 say whether the low
 *                and high halves of the pattern tables may show the
 *                nametable RAM instead of the CHR ROM
 *   $F000        PRG bank for $C000 (6 bits); bits 6-7 drive pin 44
 *   $F800        write protect for the RAM at $6000-$7FFF, and the address
 *                port of the chip's own 128 bytes of RAM
 *
 * and three more registers below $6000:
 *
 *   $4800-$4FFF  data port of the chip's 128 bytes of RAM (the sound
 *                parameters, and the save data of the games that use it)
 *   $5000-$57FF  low byte of the IRQ counter
 *   $5800-$5FFF  high byte of the IRQ counter, and its enable bit
 *
 * The IRQ counter is a 15-bit up-counter clocked by CPU cycles. It counts
 * while it is enabled and fires the IRQ when it reaches $7FFF, where it
 * stops until the game writes the counter again. A write to either counter
 * register acknowledges a pending IRQ; a read does not.
 *
 * The board can also show the console's nametable RAM in the pattern
 * tables: a CHR register written with a value of $E0 or more selects the
 * nametable RAM instead of the CHR ROM, when the matching $E800 bit allows
 * it. *Namco Classic II* draws its whole picture that way, so the RAM has
 * to follow the writes the game makes to the name tables.
 * ------------------------------------------------------------------ */

var Tile = require("../tile");

// The chip's own RAM: the sound parameters of the three channels, and the
// save data of the games that use the chip's RAM instead of a battery.
var CHIP_RAM_SIZE = 0x80;

function register(Mappers) {
  Mappers[19] = function (nes) {
    this.nes = nes;
  };

  Mappers[19].prototype = new Mappers[0]();

  // The IRQ counter is clocked by CPU cycles, so NES.frame() has to hand it
  // the real cycle count of every CPU burst.
  Mappers[19].prototype.watchesCpuCycles = true;

  // The board drives the nametable arrangement itself.
  Mappers[19].prototype.ownsMirroring = true;

  Mappers[19].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // PRG: three switchable 8 KB windows, the last one fixed at $E000.
    this.prgBank0 = 0; // $8000-$9FFF
    this.prgBank1 = 0; // $A000-$BFFF
    this.prgBank2 = 0; // $C000-$DFFF

    // CHR: eight 1 KB windows for PPU $0000-$1FFF.
    this.chrBanks = [0, 0, 0, 0, 0, 0, 0, 0];
    // Whether a window shows the console's nametable RAM instead of the CHR
    // ROM, and which of the two pages it shows.
    this.chrNametable = [
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
    ];
    this.chrNametablePage = [0, 0, 0, 0, 0, 0, 0, 0];
    // $E800 bits 6 and 7: whether the low and the high half of the pattern
    // tables may show the nametable RAM at all.
    this.chrNametableLow = false;
    this.chrNametableHigh = false;

    // Which of the two nametable pages shows in each screen area.
    this.nametablePages = [0, 1, 0, 1];

    // The chip's own 128 bytes, its address port and the auto-increment flag.
    this.chipRam = new Uint8Array(CHIP_RAM_SIZE);
    this.chipRamAddress = 0;
    this.chipRamAutoIncrement = false;

    // The IRQ counter: bit 15 is the enable, bits 0-14 the counter.
    this.irqCounter = 0;
    this.irqPending = false;

    // $F800: write protect for the RAM at $6000-$7FFF.
    this.writeProtect = 0;

    // The sound module (when the emulator made one) keeps its own state.
    if (this.sound) {
      this.sound.reset();
    }
  };

  Mappers[19].prototype.write = function (address, value) {
    // The registers below $6000: the chip's RAM and its IRQ counter.
    if (address >= 0x4800 && address < 0x6000) {
      if ((address & 0xf800) === 0x5000) {
        // Low byte of the IRQ counter. Writing it acknowledges the IRQ.
        this.irqCounter = (this.irqCounter & 0xff00) | value;
        this.irqPending = false;
      } else if ((address & 0xf800) === 0x5800) {
        // High byte of the counter, and the enable bit in bit 7.
        this.irqCounter = (this.irqCounter & 0x00ff) | (value << 8);
        this.irqPending = false;
      } else {
        // $4800-$4FFF: the data port of the chip's RAM.
        this.chipRam[this.chipRamAddress & 0x7f] = value;
        if (this.chipRamAutoIncrement) {
          this.chipRamAddress = (this.chipRamAddress + 1) & 0x7f;
        }
      }
      return;
    }

    if (address < 0x6000) {
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    if (address < 0x8000) {
      // The external RAM at $6000-$7FFF, which the board can write-protect
      // page by page. The write protect register lives at $F800: the upper
      // nybble has to be $4 for any write to get through, and bits 0-3 lock
      // the four 2 KB pages.
      if (!this.ramWritable(address)) {
        return;
      }
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    // The registers above $8000, one per $800 block.
    var block = address & 0xf800;

    if (block >= 0x8000 && block <= 0xb800) {
      // CHR: one 1 KB window per block.
      this.setChrBank((block - 0x8000) >> 11, value);
      return;
    }

    if (block >= 0xc000 && block <= 0xd800) {
      // $C000-$DFFF: which of the console's two nametable pages shows in
      // each of the four screen areas. The value's low bit is the page.
      this.setNametablePage((block - 0xc000) >> 11, value & 0x01);
      return;
    }

    switch (block) {
      case 0xe000:
        // PRG bank for $8000-$9FFF, and bit 6 disables the sound.
        this.prgBank0 = value & 0x3f;
        this.updatePrgBanks();
        if (this.sound) {
          this.sound.disableSound = (value & 0x40) !== 0;
        }
        break;

      case 0xe800:
        // PRG bank for $A000-$BFFF. Bits 6 and 7 say whether the low and
        // the high half of the pattern tables may show the nametable RAM.
        this.prgBank1 = value & 0x3f;
        this.chrNametableLow = (value & 0x40) === 0;
        this.chrNametableHigh = (value & 0x80) === 0;
        this.updatePrgBanks();
        this.updateChrBanks();
        break;

      case 0xf000:
        // PRG bank for $C000-$DFFF.
        this.prgBank2 = value & 0x3f;
        this.updatePrgBanks();
        break;

      case 0xf800:
        // Write protect for the external RAM, and the address port of the
        // chip's own RAM.
        this.writeProtect = value;
        this.chipRamAddress = value & 0x7f;
        this.chipRamAutoIncrement = (value & 0x80) !== 0;
        break;
    }
  };

  // Reads of the two counter registers give the game the counter's value.
  Mappers[19].prototype.load = function (address) {
    if (address >= 0x4800 && address < 0x6000) {
      if ((address & 0xf800) === 0x5000) {
        return this.irqCounter & 0xff;
      }
      if ((address & 0xf800) === 0x5800) {
        return (this.irqCounter >> 8) & 0xff;
      }
      // The data port of the chip's RAM.
      var value = this.chipRam[this.chipRamAddress & 0x7f];
      if (this.chipRamAutoIncrement) {
        this.chipRamAddress = (this.chipRamAddress + 1) & 0x7f;
      }
      return value;
    }
    return Mappers[0].prototype.load.apply(this, arguments);
  };

  // True when the CPU may write the external RAM at this address.
  Mappers[19].prototype.ramWritable = function (address) {
    // The upper nybble of the write protect register has to be $4, and the
    // bit of the 2 KB page the address falls in must be clear.
    if ((this.writeProtect & 0xf0) !== 0x40) {
      return false;
    }
    var page = (address >> 11) & 0x03; // $6000, $6800, $7000, $7800
    return (this.writeProtect & (1 << page)) === 0;
  };

  // One CHR window: a value of $E0 or more selects the console's nametable
  // RAM instead of the CHR ROM, and only when the matching $E800 bit allows
  // it. Otherwise the value is the number of the 1 KB CHR page.
  Mappers[19].prototype.setChrBank = function (window, value) {
    var low = window < 4;
    var allowed = low ? this.chrNametableLow : this.chrNametableHigh;

    if (value >= 0xe0 && allowed) {
      this.chrNametable[window] = true;
      this.chrNametablePage[window] = value & 0x01;
    } else {
      this.chrNametable[window] = false;
      this.chrBanks[window] = value & 0xff;
    }
    this.updateChrBanks();
  };

  // Which nametable page shows in one of the four screen areas.
  Mappers[19].prototype.setNametablePage = function (area, page) {
    this.nametablePages[area] = page;
    this.nes.ppu.setNametablePages(this.nametablePages);
  };

  Mappers[19].prototype.updatePrgBanks = function () {
    var count8k = this.nes.rom.romCount * 2; // romCount is 16K banks
    this.load8kRomBank(this.prgBank0, 0x8000);
    this.load8kRomBank(this.prgBank1, 0xa000);
    this.load8kRomBank(this.prgBank2, 0xc000);
    this.load8kRomBank(count8k - 1, 0xe000); // the last bank is fixed
  };

  Mappers[19].prototype.updateChrBanks = function () {
    for (var i = 0; i < 8; i++) {
      if (this.chrNametable[i]) {
        this.loadNametableAsChr(i);
      } else {
        this.load1kVromBank(this.chrBanks[i], i * 0x400);
      }
    }
  };

  // Copies the console's nametable RAM into a CHR window and decodes the
  // tiles it holds. The tiles the PPU's cache normally holds belong to the
  // CHR ROM, so these windows get tiles of their own.
  Mappers[19].prototype.loadNametableAsChr = function (window) {
    var ppu = this.nes.ppu;
    var source = 0x2000 + this.chrNametablePage[window] * 0x400;
    var target = window * 0x400;

    ppu.triggerRendering();
    for (var i = 0; i < 0x400; i++) {
      ppu.vramMem[target + i] = ppu.vramMem[source + i];
    }
    for (var t = 0; t < 64; t++) {
      ppu.ptTile[(target >> 4) + t] = this.decodeTile(target + t * 16);
    }
  };

  // Decodes 16 bytes of pattern data into a tile of its own.
  Mappers[19].prototype.decodeTile = function (address) {
    var ppu = this.nes.ppu;
    var tile = new Tile();
    for (var line = 0; line < 8; line++) {
      tile.setScanline(
        line,
        ppu.vramMem[address + line],
        ppu.vramMem[address + 8 + line],
      );
    }
    return tile;
  };

  // The PPU calls this when the game writes to the name tables: on this
  // board the name tables and the pattern tables are the same RAM, so the
  // window that shows them has to follow the write.
  Mappers[19].prototype.onNametableWrite = function (address) {
    if (address < 0x2000 || address >= 0x3000) {
      return;
    }
    var page = address < 0x2400 ? 0 : 1;
    var offset = address - 0x2000 - page * 0x400;
    var ppu = this.nes.ppu;

    for (var w = 0; w < 8; w++) {
      if (!this.chrNametable[w] || this.chrNametablePage[w] !== page) {
        continue;
      }
      var target = w * 0x400 + offset;
      for (var i = 0; i < 16; i++) {
        ppu.vramMem[target + i] =
          ppu.vramMem[0x2000 + page * 0x400 + offset + i];
      }
      ppu.ptTile[target >> 4] = this.decodeTile(target & ~0x0f);
    }
  };

  Mappers[19].prototype.clockCpuCycles = function (cycles) {
    // The counter counts while it is enabled, and the IRQ comes when the low
    // byte wraps.
    //
    // NESdev describes the counter as fifteen bits wide, with the interrupt
    // raised when it reaches $7FFF. The games that use the interrupt say
    // otherwise: *Namco Classic II* arms the counter and expects the
    // interrupt a couple of hundred cycles later -- it uses it as a raster
    // split and arms it right where it wants the line. With the fifteen-bit
    // reading the interrupt comes tens of thousands of cycles too late, the
    // split never lands where it should, and the game hangs waiting for an
    // interrupt that has already passed.
    if ((this.irqCounter & 0x8000) === 0) {
      return;
    }
    for (var i = 0; i < cycles; i++) {
      if ((this.irqCounter & 0xff) === 0xff) {
        break;
      }
      this.irqCounter++;
      if ((this.irqCounter & 0xff) === 0xff && !this.irqPending) {
        this.irqPending = true;
        this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
      }
    }
  };

  Mappers[19].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("Namco 163: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on layout: the three switchable windows start on the first
    // banks, and the last 8 KB of the image is fixed at $E000 where the
    // reset vector lives.
    this.updatePrgBanks();
    this.updateChrBanks();

    // The board chooses the nametable arrangement itself, but until the game
    // writes the registers the console's own arrangement stands.
    this.nes.ppu.setNametablePages(this.nametablePages);

    this.resetIrqHandler();
  };

  Mappers[19].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgBank0 = this.prgBank0;
    s.prgBank1 = this.prgBank1;
    s.prgBank2 = this.prgBank2;
    s.chrBanks = this.chrBanks.slice();
    s.chrNametable = this.chrNametable.slice();
    s.chrNametablePage = this.chrNametablePage.slice();
    s.chrNametableLow = this.chrNametableLow;
    s.chrNametableHigh = this.chrNametableHigh;
    s.nametablePages = this.nametablePages.slice();
    s.chipRam = Array.prototype.slice.call(this.chipRam);
    s.chipRamAddress = this.chipRamAddress;
    s.chipRamAutoIncrement = this.chipRamAutoIncrement;
    s.irqCounter = this.irqCounter;
    s.irqPending = this.irqPending;
    s.writeProtect = this.writeProtect;
    return s;
  };

  Mappers[19].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgBank0 = s.prgBank0;
    this.prgBank1 = s.prgBank1;
    this.prgBank2 = s.prgBank2;
    this.chrBanks = s.chrBanks.slice();
    this.chrNametable = s.chrNametable.slice();
    this.chrNametablePage = s.chrNametablePage.slice();
    this.chrNametableLow = s.chrNametableLow;
    this.chrNametableHigh = s.chrNametableHigh;
    this.nametablePages = s.nametablePages.slice();
    this.chipRam = new Uint8Array(s.chipRam);
    this.chipRamAddress = s.chipRamAddress;
    this.chipRamAutoIncrement = s.chipRamAutoIncrement;
    this.irqCounter = s.irqCounter;
    this.irqPending = s.irqPending;
    this.writeProtect = s.writeProtect;
    // The CPU's copy of the ROM windows and the PPU's copy of the pattern
    // windows are not part of the mapper's state, so re-derive them.
    this.updatePrgBanks();
    this.updateChrBanks();
    this.nes.ppu.setNametablePages(this.nametablePages);
  };
}

module.exports = { register: register };
